import { Users2, MapPin, Calendar, Megaphone, Check } from 'lucide-react';
import type { XpStructureEvent, XpStructureDemographic } from '@/lib/api/xamplin';

/**
 * Launch readiness + persona panel (build-v2 G5/G6). Readiness derives 4 stages
 * from the project structure — Structure, Stations, Staffing, Dates — and rolls
 * them into a single "ready to launch" percentage, mirroring the prototype
 * `ReadinessMeter`. PersonaPanel renders the project's 2-persona demographics.
 */
export interface ReadinessStage {
  key: string;
  label: string;
  icon: React.ReactNode;
  done: number;
  total: number;
}

export function computeReadiness(events: XpStructureEvent[]): ReadinessStage[] {
  const total = events.length || 1;
  const withStations = events.filter((e) => (e.stations?.length ?? 0) > 0).length;
  const withReporters = events.filter((e) => (e.reporters?.length ?? 0) > 0).length;
  const withDates = events.filter((e) => !!e.startDate).length;
  return [
    { key: 'structure', label: 'Structure', icon: <Megaphone className="h-3.5 w-3.5" />, done: events.length, total },
    { key: 'stations', label: 'Stations', icon: <MapPin className="h-3.5 w-3.5" />, done: withStations, total },
    { key: 'staffing', label: 'Staffing', icon: <Users2 className="h-3.5 w-3.5" />, done: withReporters, total },
    { key: 'dates', label: 'Dates', icon: <Calendar className="h-3.5 w-3.5" />, done: withDates, total },
  ];
}

export function readinessPct(stages: ReadinessStage[]): number {
  if (!stages.length) return 0;
  const sum = stages.reduce((s, st) => s + (st.total ? st.done / st.total : 0), 0);
  return Math.round((sum / stages.length) * 100);
}

export function ReadinessMeter({ events, compact }: { events: XpStructureEvent[]; compact?: boolean }) {
  const stages = computeReadiness(events);
  const pct = readinessPct(stages);
  const ready = pct >= 100;
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-4">
      <div className="flex items-center gap-2">
        <h3 className="mono text-xs font-bold uppercase tracking-wider text-[var(--color-xk-text-muted)]">
          Launch readiness
        </h3>
        <span
          className="mono ml-auto rounded-full px-2 py-0.5 text-[11px] font-bold"
          style={{
            color: ready ? '#4dd28b' : 'var(--color-xk-accent)',
            background: (ready ? '#4dd28b' : 'var(--color-xk-accent)') + '1a',
          }}
        >
          {ready ? 'ready to launch' : `${pct}% ready`}
        </span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-[var(--color-xk-bg)]">
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${pct}%`, background: ready ? '#4dd28b' : 'var(--color-xk-accent)' }}
        />
      </div>
      {!compact && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          {stages.map((s) => {
            const ok = s.total > 0 && s.done >= s.total;
            return (
              <div key={s.key} className="flex items-center gap-2 rounded-lg border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] px-2.5 py-2">
                <span className={ok ? 'text-[#4dd28b]' : 'text-[var(--color-xk-text-muted)]'}>
                  {ok ? <Check className="h-3.5 w-3.5" /> : s.icon}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-[11px] text-[var(--color-xk-lavender)]">{s.label}</div>
                  <div className="mono text-[9px] text-[var(--color-xk-text-subtle)]">{s.done}/{s.total}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const PERSONA_COLORS = ['#B262F5', '#5aa9ff'];
export function PersonaPanel({ demographics }: { demographics: XpStructureDemographic[] }) {
  if (!demographics || demographics.length === 0) return null;
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] p-4">
      <h3 className="mono text-xs font-bold uppercase tracking-wider text-[var(--color-xk-text-muted)]">
        Target personas
      </h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {demographics.slice(0, 2).map((d, i) => (
          <div key={d.id} className="rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-bg)] p-3">
            <div className="flex items-center gap-2">
              <span className="grid h-7 w-7 place-items-center rounded-full text-[11px] font-extrabold text-white" style={{ background: PERSONA_COLORS[i % 2] }}>
                {(d.name ?? 'P').slice(0, 1).toUpperCase()}
              </span>
              <span className="truncate text-sm font-semibold text-[var(--color-xk-lavender)]">{d.name || `Persona ${i + 1}`}</span>
            </div>
            <div className="mono mt-2 flex flex-col gap-1 text-[10px] text-[var(--color-xk-text-muted)]">
              {d.ageGroup && <span>Edad · {d.ageGroup}</span>}
              {d.gender && <span>Género · {d.gender}</span>}
              {d.ethnicity && <span>Etnia · {d.ethnicity}</span>}
              {d.expectedAudience ? <span>Audiencia · {d.expectedAudience.toLocaleString()}</span> : null}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
